import type { ColumnDef } from "@tanstack/react-table";
import CommentIcon from "@mui/icons-material/Comment";
import { OrderLineTypes } from "@repo/database";
import { formatIntToCurrency, highlightText } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { useDoubleTap } from "use-double-tap";

import type { CartItem } from "~/providers/cart-provider";
import { Checkbox } from "~/components/ui/checkbox";
import { cn } from "~/lib/utils";
import { useCartManager } from "~/managers/use-cart-manager";
import { useCourse } from "./use-course";

const QuantityColumn = ({ item }: { item: CartItem }) => {
  const { addToCart } = useCartManager();
  const bind = useDoubleTap(async () => {
    // Your action here
    await addToCart({
      ...item,
      quantity: item.quantity + 1,
      ordered: false,
    });
  });
  return (
    <div {...bind} className="flex flex-row items-center gap-1 justify-end">
      <span
        className={cn(
          item.ordered === false && "font-bold",
          item.quantity === 0 && "line-through",
        )}
        dangerouslySetInnerHTML={{
          __html:
            highlightText(formatIntToCurrency(item.quantity, false)) ?? "",
        }}
      ></span>
      <span className="text-[8px]">X</span>
    </div>
  );
};

export const useCartTable = ({
  canSelect,
  items,
  onSelect,
}: {
  canSelect?: boolean;
  onSelect?: (item: CartItem) => void;
  items: CartItem[];
}) => {
  const { courses } = useCourse();

  const { t } = useTranslation("common");

  const columns: ColumnDef<CartItem>[] = [
    {
      id: t("course"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 5,
      cell: ({ row: { original: item } }) => {
        return (
          <div
            style={{
              backgroundColor:
                courses.find((course) => course.id === item.courseId)?.color ??
                "",
            }}
            className={cn(
              "rounded-full w-4 h-4 border-black border opacity-0",
              !item.parentReference && "opacity-100",
              item.quantity === 0 && "line-through",
            )}
          ></div>
        );
      },
    },
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 500,
      cell: ({ row: { original: item }, table }) => {
        const globalFilter = table.getState().globalFilter as string;
        return (
          <div className="flex flex-row items-center gap-1">
            {item.parentReference && (
              <>
                {item.type === OrderLineTypes.product && "+"}
                {item.type === OrderLineTypes.comment && (
                  <CommentIcon fontSize="small" />
                )}
              </>
            )}

            <p
              className={cn(
                "line-clamp-2 text-ellipsis break-all text-start text-sm",
                item.ordered === false && "font-bold",
                item.quantity === 0 && "line-through",
              )}
              dangerouslySetInnerHTML={{
                __html: highlightText(item.name, globalFilter) ?? "",
              }}
            />
          </div>
        );
      },
    },
    // {
    //   id: t("quantity_short"),
    //   accessorKey: "quantity",
    //   filterFn: "includesString",
    //   size: 100,
    //   meta: {
    //     canSelect: false,
    //   },
    //   cell: ({ row: { original: item } }) => {
    //     return <QuantityColumn item={item} />;
    //   },
    // },
    {
      id: t("price"),
      accessorKey: "value",
      filterFn: "includesString",
      size: 100,
      cell: ({ row: { original: item }, table }) => {
        const globalFilter = table.getState().globalFilter as string;
        return (
          <div className="flex flex-row items-center justify-end gap-1">
            {(item.type === OrderLineTypes.product ||
              item.type === OrderLineTypes.extra) && (
              <>
                <span className="text-[12px]">€</span>
                <p
                  dangerouslySetInnerHTML={{
                    __html:
                      highlightText(
                        formatIntToCurrency(item.value * item.quantity, false),
                        globalFilter,
                      ) ?? "",
                  }}
                  className={cn(
                    "text-sm text-right font-bold",
                    item.ordered && "font-light",
                    item.quantity === 0 && "line-through",
                  )}
                >
                  {}
                </p>
              </>
            )}
            {item.type === OrderLineTypes.discount && (
              <p
                className={cn(
                  "text-sm font-bold text-right",
                  item.ordered && "font-light",
                  item.quantity === 0 && "line-through",
                )}
                dangerouslySetInnerHTML={{
                  __html:
                    highlightText(
                      `- ${formatIntToCurrency(item.value, false)}`,
                      globalFilter,
                    ) ?? "",
                }}
              ></p>
            )}
          </div>
        );
      },
    },
  ];

  if (canSelect) {
    columns.unshift({
      id: "select",
      accessorKey: "name",
      size: 100,
      cell: ({ row }) => {
        if (row.original.parentReference) return null;

        return (
          <Checkbox
            checked={items.some(
              (item) => item.reference === row.original.reference,
            )}
            onCheckedChange={() => {
              if (onSelect) {
                onSelect(row.original);
              }
            }}
            aria-label="Select row"
          />
        );
      },
    });
  }

  return {
    columns,
  };
};
